import { find, isEmpty } from 'lodash';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  DASHBOARD_GRAPH_AUCTIONS,
  DASHBOARD_GRAPH_BUYERS,
  DashboardGraphID,
  getDashboardGraphID,
  getItemsKey,
  getUpdatedEndDate,
  IDashboardChartData,
  IDashboardHomeCardMetrics,
  IDashboardOverviewData,
  IDashboardTopChannelsParams,
  IDashboardTopDemandSourcesParams,
  LiveItemsKey,
  matchEndDateHMS,
  queryHomeDashboardData,
  updateCompareEndDate,
} from '~/src/dash/api/dashboards/home';
import { getSmallBiddersList } from '~/src/dash/api/filters/bidders';
import { getFilteredDemandSourcesWithBidderTypes } from '~/src/dash/api/filters/demandSources';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { DashboardCardsV2 } from '~/src/dash/components/cards/DashboardCardsV2';
import { IDashboardChartMetric, mergeCompareData } from '~/src/dash/components/charts/common';
import { CompareChart } from '~/src/dash/components/charts/CompareChart';
import {
  FILTER_PILLS,
  FilterPillKey,
  formatSelectedBidderGroupIntoStringOfIDs,
  formatSelectedChannelGroupIntoStringOfIDs,
  formatSelectedDestinationsIntoStringOfSiteIDs,
  formatSelectedRouteIntoStringOfSiteIDs,
  getDashboardFilterParams,
  getInitialDashboardFilterParams,
} from '~/src/dash/components/filters/common';
import { CalendarIcon, RefreshIcon } from '~/src/dash/components/icons/misc-icons';
import { MenuToggle } from '~/src/dash/components/toggles/MenuToggle';
import { DensityRangeHourByHour } from '~/src/dash/globals/constants';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import {
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  areDateRangesMatchingByDay,
  formatDateEndDay,
  formatDateStartDay,
  isDateToday,
  isDateTodayWithTimezone,
} from '~/src/dash/utils/date';
import { EDateGroup } from '~/src/dash/utils/dateGroups';
import { DateRange, findDateRange, generateQueryDates } from '~/src/dash/utils/dateTimeRanges';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { getTimezone } from '~/src/dash/utils/timezone';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  addDefaultSelected,
  clearedActiveFilters,
  DashboardTrendsSticker,
  extractViableMetrics,
  formatDataToCSV,
  generateCSVColumns,
  generateCSVTitle,
  generateCustomCompareDates,
  generateCustomDateRange,
  generateInventoryRoutesAndDestinations,
  isBidderTypeSelected,
  numSitesBidders,
  refreshBiddersFilter,
  refreshDemandSourcesFilter,
  updateActiveChartMetrics,
  updateDefaultSelectedForFilters,
  updateDisplayedMetricKeys,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { DashboardFilterMenuToggle } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardFilterMenuToggle';
import { DashboardFilters } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardFilters';
import { DashboardPeriods } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardPeriods';
import { DashboardSelector } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardSelector';
import { DateGroupToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateGroupToggles';
import { IDashboardLiveConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardLiveConfig';
import { IDashboardOverviewConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardOverviewConfig';
import { CSVDownloadButton } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardOverview/CSVDownloadButton';
import { TopChannelsTable } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardOverview/TopChannelsTable';
import { TopDemandSourcesTable } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardOverview/TopDemandSourcesTable';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const convertItemsKeyForTopChannels = (key: LiveItemsKey): LiveItemsKey => {
  switch (key) {
    case 'site_bidder':
    case 'bidder':
      return 'site_bidder';
    case 'site_biddername':
    case 'biddername':
      return 'site_biddername';
    case 'site_device_type':
    case 'device_type':
      return 'site_device_type';
    default:
      return 'site';
  }
};

const convertItemsKeyForTopDemandSources = (key: LiveItemsKey): LiveItemsKey => {
  switch (key) {
    case 'bidder':
    case 'site_bidder':
      return key;
    case 'site_biddername':
    case 'site':
      return 'site_biddername';
    case 'biddername_device_type':
    case 'device_type':
      return 'biddername_device_type';
    default:
      return 'biddername';
  }
};

// IMPORTANT: IF YOU ARE UPDATING OVERVIEW, CHECK IF PARENT OVERVIEW MUST BE UPDATED TOO
// IMPORTANT: PLEASE READ THE README BEFORE EDITING -> https://publica-project.atlassian.net/wiki/x/DQDXpQ
export const DashboardOverview = () => {
  const initialLoadStart = moment().unix();
  const { Dashboards } = useBaseConfig();
  const cfg = Dashboards.Overview as IDashboardOverviewConfig;
  const liveCfg = Dashboards.Live as IDashboardLiveConfig;
  const { cards, defaultState } = cfg;
  const { session, user, currencyType, isSuperUser } = useBaseStore();

  // ensure there are no deals from activeFilters when loading overview
  defaultState.activeFilters.deal = '';
  const initialGraphID = getDashboardGraphID(defaultState.activeFilters);
  const initialDisplayedMetricKeys = updateDisplayedMetricKeys(
    cards.groupings[initialGraphID],
    defaultState.displayedMetricKeys,
  );
  const initialActiveChartMetrics = updateActiveChartMetrics(
    initialDisplayedMetricKeys,
    defaultState.activeChartMetrics,
    cards.metrics,
  );
  const defaultTimezone =
    defaultState.timezone || getTimezone(user.user.Timezone, session.publisher.Timezone);
  const initialQueryDates = generateQueryDates(defaultState.dateRange, defaultTimezone);

  const isMounted = useRef(false);
  const isInitialized = useRef(false);
  const hasInitialQueryCompleted = useRef(false);
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [dateRange, setDateRange] = useState(findDateRange(defaultState.dateRange));
  const [grouping, setGrouping] = useState(defaultState.grouping);
  const [graphID, setGraphID] = useState(initialGraphID);
  const [calStartDate, setCalStartDate] = useState(initialQueryDates.startDate);
  const [calEndDate, setCalEndDate] = useState(initialQueryDates.endDate);
  const [isLiveDataEnabled, setIsLiveDataEnabled] = useState(
    isDateToday(initialQueryDates.endDate, timezone),
  );
  const [isCompareEnabled, setIsCompareEnabled] = useState(defaultState.isCompareEnabled);
  const [calCompareStartDate, setCalCompareStartDate] = useState(
    initialQueryDates.compareStartDate,
  );
  const [calCompareEndDate, setCalCompareEndDate] = useState(initialQueryDates.compareEndDate);
  const [filtersLoadState, setFiltersLoadState] = useState(LoadingStateLoading);
  const [filters, setFilters] = useState({} as Record<string, IMenuList>);
  const [selectedFilters, setSelectedFilters] = useState({} as Record<string, IMenuList>);
  const [isDemandRefreshing, setIsDemandRefreshing] = useState(false);
  const [activeFilters, setActiveFilters] = useState(defaultState.activeFilters);
  const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(defaultState.isPeriodMenuOpen);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(defaultState.isFilterMenuOpen);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [displayedMetricKeys, setDisplayedMetricKeys] = useState(initialDisplayedMetricKeys);
  const [activeChartMetrics, setActiveChartMetrics] = useState(initialActiveChartMetrics);
  const [topChannelsParams, setTopChannelsParams] = useState({} as IDashboardTopChannelsParams);
  const [topDemandSourcesParams, setTopDemandSourcesParams] = useState(
    {} as IDashboardTopDemandSourcesParams,
  );
  const [tableRowsPerPage, setTableRowsPerPage] = useState(defaultState.tableRowsPerPage);
  const [data, setData] = useState({} as IDashboardOverviewData);

  const [activeFilterParams, setActiveFilterParams] = useState<Record<string, string>>({});

  const isDemandSourceUsingBidderType = useRef(false);

  useEffect(() => {
    getDashboardOverviewData();
    getFilters();
    track('Dash Overview - View', { isSuperUser }, user.user);
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      track('Dash Overview - Enable/Disable Trends', { isSuperUser, isCompareEnabled }, user.user);
    }
  }, [isCompareEnabled]);

  // if a quick select range / timezone is selected, dates must be updated
  useEffect(() => {
    if (isMounted.current && dateRange.key !== ('Custom' as DateRange)) {
      const { startDate, endDate, compareStartDate, compareEndDate } =
        dateRange.generateQueryDates(timezone);
      unstable_batchedUpdates(() => {
        setCalStartDate(startDate);
        setCalEndDate(endDate);
        setCalCompareStartDate(compareStartDate);
        setCalCompareEndDate(compareEndDate);
        setGrouping(dateRange.defaultDateGroup);
      });
    }
  }, [dateRange, timezone]);

  useEffect(() => {
    if (isMounted.current) {
      getDashboardOverviewData();
    }
  }, [
    grouping,
    calStartDate,
    calEndDate,
    calCompareStartDate,
    calCompareEndDate,
    isCompareEnabled,
  ]);

  useEffect(() => {
    if (isMounted.current && filtersLoadState === LoadingStateHasData && isInitialized.current)
      getDashboardOverviewData();
  }, [selectedFilters]);

  useEffect(() => {
    if (isMounted.current) {
      updateDefaultSelectedForFilters(selectedFilters, cfg.filters, cfg.defaultState);
      if (!isInitialized.current) isInitialized.current = true;
    }
    isMounted.current = true;
  }, [selectedFilters]);

  const getDashboardOverviewData = () => {
    const queryStart = moment().unix();
    setLoadState(LoadingStateLoading);
    const isEndDateToday = isDateTodayWithTimezone(calEndDate, timezone);

    getUpdatedEndDate(graphID, calEndDate, session.publisher.ID, timezone).then(
      ({ formattedEndDate, errorMessage }) => {
        if (errorMessage) {
          unstable_batchedUpdates(() => {
            setIsLiveDataEnabled(false);
            setSnackbarError(false);
            setSnackbarMessage(errorMessage);
          });
        }
        // update compare end date if needed
        const updatedCompareEndDate =
          isCompareEnabled &&
          isEndDateToday &&
          areDateRangesMatchingByDay(
            calStartDate,
            calEndDate,
            calCompareStartDate,
            calCompareEndDate,
          )
            ? updateCompareEndDate(calCompareEndDate, timezone, calStartDate)
            : formatDateEndDay(calCompareEndDate);

        const hasInitiallyLoaded =
          isInitialized.current && filtersLoadState !== LoadingStateLoading;

        let isUsingBidderType = false;
        if (activeFilters.demand === 'Filtered Demand Sources') {
          if (hasInitiallyLoaded) {
            isUsingBidderType = isBidderTypeSelected(selectedFilters['Filtered Demand Sources']);
          } else {
            isUsingBidderType = defaultState.isDemandSourceUsingBidderType;
          }
        }
        isDemandSourceUsingBidderType.current = isUsingBidderType;

        const liveItemsKey = getItemsKey(activeFilters, false, isUsingBidderType);

        const updatedGraphID = activeFilters.demand
          ? DASHBOARD_GRAPH_BUYERS
          : DASHBOARD_GRAPH_AUCTIONS;

        const start_date = formatDateStartDay(calStartDate);
        const compare_start_date = formatDateStartDay(calCompareStartDate);

        // create filter params object
        const filterParams = hasInitiallyLoaded
          ? getDashboardFilterParams(selectedFilters, cfg.filters, activeFilters, isUsingBidderType)
          : getInitialDashboardFilterParams(cfg.filters, activeFilters, isUsingBidderType);

        const numSiteBidderKeys = numSitesBidders(
          filterParams.site_ids || '',
          filterParams.bidder_ids || '',
        );
        const isMaxBucketsExceeded = numSiteBidderKeys > 5000;
        if (isMaxBucketsExceeded) {
          unstable_batchedUpdates(() => {
            setSnackbarMessage(
              `Live data has been disabled. Only a maximum of 5000 combinations of bidders and sites can be queried with live data (last count: ${numSiteBidderKeys})`,
            );
            setSnackbarError(true);
          });
        }

        const isLiveAllowed = isLiveDataEnabled && isEndDateToday && !isMaxBucketsExceeded;
        const formattedCompareEndDate = isLiveAllowed
          ? updatedCompareEndDate
          : matchEndDateHMS(formattedEndDate, updatedCompareEndDate);

        const query = {
          publisher_id: session.publisher.ID,
          graph_id: updatedGraphID,
          timezone,
          start_date,
          end_date: formattedEndDate,
          is_compare_enabled: isCompareEnabled,
          compare_start_date,
          compare_end_date: formattedCompareEndDate,
          grouping,
          ...filterParams,
          currency_type: currencyType,
          is_live_data_enabled: isLiveAllowed,
          is_live_data_to_be_merged: true,
          is_live_refresh_only: false,
          live_density_range: DensityRangeHourByHour,
          live_items_key: liveItemsKey,
        };

        setTopChannelsParams({
          graph_id: activeFilters.demand ? 61 : 48,
          start_date: formatDateStartDay(calStartDate),
          end_date: formattedEndDate,
          timezone,
          publisher_id: session.publisher.ID,
          site_ids: filterParams.site_ids || '',
          bidder_ids: filterParams.bidder_ids || '',
          biddercodes: filterParams.biddercodes || '',
          devicetypes: filterParams.devicetypes || '',
          statFilter: 'revenue',
          statOrder: 'desc',
          is_live_data_enabled: isLiveAllowed,
          currency_type: currencyType,
          limit: 10,
          offset: 0,
          live_items_key: convertItemsKeyForTopChannels(liveItemsKey),
        });

        setTopDemandSourcesParams({
          start_date,
          end_date: formattedEndDate,
          timezone,
          publisher_id: session.publisher.ID,
          site_ids: filterParams.site_ids || '',
          bidder_ids: filterParams.bidder_ids || '',
          biddercodes: filterParams.biddercodes || '',
          devicetypes: filterParams.devicetypes || '',
          statFilter: 'revenue',
          statOrder: 'desc',
          is_live_data_enabled: isLiveAllowed,
          currency_type: currencyType,
          limit: 10,
          offset: 0,
          live_items_key: convertItemsKeyForTopDemandSources(liveItemsKey),
        });

        queryHomeDashboardData(session.publisher.ID, query)
          .then((result) => {
            const { MainData, CompareData } = result;
            const isDataEmpty =
              (isCompareEnabled && isEmpty(MainData) && isEmpty(CompareData)) ||
              (!isCompareEnabled && isEmpty(MainData));
            unstable_batchedUpdates(() => {
              setData(result);
              setLoadState(isDataEmpty ? LoadingStateNoData : LoadingStateHasData);
              if (graphID !== updatedGraphID) updateMetrics(updatedGraphID);
              if (isDataEmpty) {
                setSnackbarError(false);
                setSnackbarMessage('No Data Found.');
              }
              setActiveFilterParams({ ...filterParams });
            });
            if (!hasInitialQueryCompleted.current) {
              track(
                'Dash Overview - Main Data Initial Query',
                {
                  isSuperUser,
                  initialLoadTime: moment().unix() - initialLoadStart,
                  activeFilters,
                  dateRange: dateRange.label,
                  grouping: EDateGroup[grouping],
                },
                user.user,
              );
              hasInitialQueryCompleted.current = true;
            } else {
              track(
                'Dash Overview - Main Data Query',
                {
                  isSuperUser,
                  queryTime: moment().unix() - queryStart,
                  activeFilters,
                  dateRange: dateRange.label,
                  grouping: EDateGroup[grouping],
                },
                user.user,
              );
            }
          })
          .catch(() => {
            unstable_batchedUpdates(() => {
              setData({
                MainData: [] as IDashboardChartData[],
                CompareData: [] as IDashboardChartData[],
                CardMetrics: {} as IDashboardHomeCardMetrics,
              });
              if (graphID !== updatedGraphID) updateMetrics(updatedGraphID);
              setLoadState(LoadingStateNoData);
              setSnackbarError(true);
              setSnackbarMessage('Error retrieving data. Please wait and try again.');
              setActiveFilterParams({});
            });
          });
      },
    );
  };

  const updateMetrics = (updatedGraphID: DashboardGraphID) => {
    if (graphID !== updatedGraphID) {
      const updatedDisplayedMetricKeys = updateDisplayedMetricKeys(
        cards.groupings[updatedGraphID],
        displayedMetricKeys,
      );
      const updatedActiveChartMetrics = updateActiveChartMetrics(
        updatedDisplayedMetricKeys,
        activeChartMetrics,
        cards.metrics,
      );
      unstable_batchedUpdates(() => {
        setDisplayedMetricKeys(updatedDisplayedMetricKeys);
        setGraphID(updatedGraphID);
        setActiveChartMetrics(updatedActiveChartMetrics);
      });
      defaultState.displayedMetricKeys = updatedDisplayedMetricKeys;
      defaultState.activeChartMetrics = updatedActiveChartMetrics;
    }
  };

  const getFilters = () => {
    setFiltersLoadState(LoadingStateLoading);
    let updatedFilters = {} as Record<FilterPillKey, IMenuList>;
    const updatedSelectedFilters = {} as Record<FilterPillKey, IMenuList>;
    const apiCalls = [] as Array<Promise<Record<string, IMenuList>>>;

    const filterKeys = cfg.orderedFilterKeys.filter(
      (key) => key !== 'Routes' && key !== 'Destinations',
    ) as FilterPillKey[];

    filterKeys.forEach((key) => {
      const { list, getList } = FILTER_PILLS[key];
      // if the filter requires an api call, load it
      if (getList) {
        if (key === 'Filtered Demand Sources') {
          apiCalls.push(
            getFilteredDemandSourcesWithBidderTypes(
              session,
              user,
              '',
              !!defaultState.activeFilters.device,
            ),
          );
        } else {
          apiCalls.push(getList(session, user));
        }
      } else if (list) {
        updatedFilters[key] = FILTER_PILLS[key].list || ({} as IMenuList);
      }
    });
    Promise.all(apiCalls)
      .then((apiFilters) => {
        for (const filter of apiFilters) {
          updatedFilters = { ...updatedFilters, ...filter };
        }
        // if there are selected defaults, make them selected true here before continuing
        addDefaultSelected(filterKeys, updatedSelectedFilters, updatedFilters, cfg.filters);
      })
      .then(() => {
        // inventory routes and destinations are handled separately because they're generated through a single api call
        generateInventoryRoutesAndDestinations(session.publisher.ID, user.user).then(
          ({ Routes, Destinations }) => {
            updatedFilters.Routes = Routes;
            updatedFilters.Destinations = Destinations;
            // if there are selected defaults, make them selected true here before continuing
            addDefaultSelected(
              ['Routes', 'Destinations'] as FilterPillKey[],
              updatedSelectedFilters,
              updatedFilters,
              cfg.filters,
            );

            const timezones = genTimezoneList(timezone);
            updatedFilters.Timezones = timezones;

            unstable_batchedUpdates(() => {
              setFilters(updatedFilters);
              setFiltersLoadState(LoadingStateHasData);
              setSelectedFilters(updatedSelectedFilters);
            });
          },
        );
      })
      .catch((err) => {
        console.error(err);
        setFiltersLoadState(LoadingStateNoData);
      });
  };

  const handleApplyDatesChange = ({ newStartDate, newEndDate }: INewCalDates) => {
    const range = newEndDate.diff(newStartDate, 'days');
    const compareDates = generateCustomCompareDates(newStartDate, newEndDate);
    const customDateRange = generateCustomDateRange(range);
    unstable_batchedUpdates(() => {
      setCalStartDate(newStartDate);
      setCalEndDate(newEndDate);
      setCalCompareStartDate(compareDates.compStartDate);
      setCalCompareEndDate(compareDates.compEndDate);
      setDateRange(customDateRange);
      setGrouping(customDateRange.defaultDateGroup);
    });
    track('Dash Overview - Use Main Calendar', { isSuperUser }, user.user);
  };

  const handleApplyCompareDatesChange = ({ newStartDate, newEndDate }: INewCalDates) => {
    unstable_batchedUpdates(() => {
      setCalCompareStartDate(newStartDate);
      setCalCompareEndDate(newEndDate);
    });
    track('Dash Overview - Use Compare Calendar', { isSuperUser }, user.user);
  };

  const handleToggleDateRange = (rangeKey: DateRange) => {
    setDateRange(findDateRange(rangeKey));
    track('Dash Overview - Select Date Shortcut', { isSuperUser, range: rangeKey }, user.user);
  };

  const handleAcceptFilter = async (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => {
    setLoadState(LoadingStateLoading);
    const appliedFilter = cfg.filters[key];
    const { category } = appliedFilter;
    const clonedActiveFilters = cloneDeep(activeFilters);
    if (!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
      if (appliedFilter.isGroup) appliedFilter.defaultGroupSelected = [];
    } else if (!!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
      if (appliedFilter.isGroup) appliedFilter.defaultGroupSelected = [];
      if (category && clonedActiveFilters[category] === key) {
        clonedActiveFilters[category] = '';
      }
    } else {
      appliedFilter.defaultSelected = Object.values(selectedOptions).map((item) => item.ID);
      if (category) clonedActiveFilters[category] = key;
    }

    // custom block for refreshing bidders & demand sources
    const updatedSelectedFilters = {} as Record<string, IMenuList>;
    const updatedFilters = {} as Record<string, IMenuList>;
    if (key === 'Bidder Groups') {
      const bidderIDs = formatSelectedBidderGroupIntoStringOfIDs(selectedOptions);
      appliedFilter.defaultGroupSelected = bidderIDs.split(',');
    }
    if (category === 'supply' || category === 'device') {
      unstable_batchedUpdates(() => {
        // we set this early here to prevent a color change delay while the demand dropdowns refresh
        setActiveFilters(clonedActiveFilters);
        setIsDemandRefreshing(true);
      });
      let siteIDs = '';
      switch (clonedActiveFilters.supply) {
        case 'Channels':
          siteIDs = Object.keys(selectedOptions).join(',');
          break;
        case 'Channel Groups':
          siteIDs = formatSelectedChannelGroupIntoStringOfIDs(selectedOptions);
          appliedFilter.defaultGroupSelected = siteIDs.split(',');
          break;
        case 'Routes':
          siteIDs = formatSelectedRouteIntoStringOfSiteIDs(selectedOptions);
          break;
        case 'Destinations':
          siteIDs = formatSelectedDestinationsIntoStringOfSiteIDs(selectedOptions);
          break;
        default:
          break;
      }
      if (category === 'supply') {
        const results = await Promise.all([
          refreshBiddersFilter(session, user, selectedFilters.Bidders, siteIDs),
          refreshDemandSourcesFilter(
            session,
            user,
            selectedFilters['Filtered Demand Sources'],
            siteIDs,
          ),
        ]);
        const { selectedBidders, allBidders } = results[0];
        const { selectedDemandSources, allDemandSources } = results[1];
        updatedSelectedFilters.Bidders = selectedBidders;
        updatedFilters.Bidders = allBidders;
        if (isEmpty(selectedBidders) && activeFilters.demand === 'Bidders') {
          clonedActiveFilters.demand = '';
        }
        updatedSelectedFilters['Filtered Demand Sources'] = selectedDemandSources;
        updatedFilters['Filtered Demand Sources'] = allDemandSources;
        if (isEmpty(selectedDemandSources) && activeFilters.demand === 'Filtered Demand Sources') {
          clonedActiveFilters.demand = '';
        }
      }
      if (category === 'device') {
        const result = await refreshDemandSourcesFilter(
          session,
          user,
          selectedFilters['Filtered Demand Sources'],
          siteIDs,
          !isEmpty(selectedOptions),
        );
        const { selectedDemandSources, allDemandSources } = result;
        updatedSelectedFilters['Filtered Demand Sources'] = selectedDemandSources;
        updatedFilters['Filtered Demand Sources'] = allDemandSources;
        if (isEmpty(selectedDemandSources) && activeFilters.demand === 'Filtered Demand Sources') {
          clonedActiveFilters.demand = '';
        }
      }
    }

    unstable_batchedUpdates(() => {
      setFilters({ ...filters, [key]: allOptions, ...updatedFilters });
      setSelectedFilters((selectedFilters) => ({
        ...selectedFilters,
        [key]: selectedOptions,
        ...updatedSelectedFilters,
      }));

      setActiveFilters({ ...clonedActiveFilters });
      setIsDemandRefreshing(false);
    });
    defaultState.activeFilters = clonedActiveFilters;
    track('Dash Overview - Use Filter', { isSuperUser, filter: key }, user.user);
  };

  const handleClearAllFilters = async () => {
    setLoadState(LoadingStateLoading);
    const clonedFilters = cloneDeep(filters);
    // if a supply filter was active, we must reload demand sources and bidders
    if (activeFilters.supply || activeFilters.device) {
      setIsDemandRefreshing(true);
      await Promise.all([
        getFilteredDemandSourcesWithBidderTypes(session, user),
        getSmallBiddersList(session, user),
      ]).then((result) => {
        clonedFilters['Filtered Demand Sources'] = result[0]['Filtered Demand Sources'];
        selectedFilters['Filtered Demand Sources'] = {};
        clonedFilters.Bidders = result[1].Bidders;
        selectedFilters.Bidders = {};
      });
    }
    const selectedFilterKeys = Object.keys(selectedFilters);
    selectedFilterKeys.forEach((filterKey) => {
      if (filterKey === 'Timezones') return;
      const selectedItemKeys = Object.keys(selectedFilters[filterKey]);
      selectedItemKeys.forEach((key) => {
        clonedFilters[filterKey][key].selected = false;
      });
      cfg.filters[filterKey].defaultSelected = [];
    });
    unstable_batchedUpdates(() => {
      setFilters(clonedFilters);
      setSelectedFilters({} as Record<string, IMenuList>);
      setActiveFilters(clearedActiveFilters);
      setIsDemandRefreshing(false);
    });
    isDemandSourceUsingBidderType.current = false;
    defaultState.activeFilters = clearedActiveFilters;
  };

  const handleChangeTimezone = (
    _key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => {
    if (isEmpty(selectedOptions)) return;

    const updatedTimezone = Object.values(selectedOptions)[0].ID;
    if (updatedTimezone === timezone) return;

    const clonedFilters = cloneDeep(filters);
    clonedFilters.Timezones = allOptions;

    unstable_batchedUpdates(() => {
      setTimezone(updatedTimezone);
      setFilters({ ...filters, Timezones: allOptions });
    });

    defaultState.timezone = updatedTimezone;
  };

  const handleSelectDateGroup = (selectedDateGroup: number) => {
    setGrouping(selectedDateGroup);
    defaultState.grouping = selectedDateGroup;
    track(
      'Dash Overview - Select Date Group',
      { isSuperUser, dateGroup: EDateGroup[selectedDateGroup] },
      user.user,
    );
  };

  const handleOnChangeCardMetric = (
    metricKey: string,
    index: number,
    isDisplayedOnChart: boolean = false,
  ) => {
    let updatedActiveChartMetrics: IDashboardChartMetric[];
    const clonedDisplayedMetricKeys = [...displayedMetricKeys];
    // if active, replace active metric
    if (isDisplayedOnChart) {
      const clonedActiveChartMetrics = [...activeChartMetrics];
      const prevMetric = clonedDisplayedMetricKeys[index];
      updatedActiveChartMetrics = clonedActiveChartMetrics.filter(
        (metric) => metric.key !== prevMetric,
      );
      const key = metricKey as keyof IDashboardHomeCardMetrics;
      const selectedMetric = cfg.cards.metrics[key];
      updatedActiveChartMetrics.push({
        key: metricKey,
        label: selectedMetric.label,
        valueType: selectedMetric.valueType,
        chartColorIndex: index,
        chartType: selectedMetric.chartType,
        yScale: selectedMetric.yScale || 'base',
      });
    }
    clonedDisplayedMetricKeys[index] = metricKey as keyof IDashboardHomeCardMetrics;
    unstable_batchedUpdates(() => {
      setDisplayedMetricKeys(clonedDisplayedMetricKeys);
      if (isDisplayedOnChart) {
        setActiveChartMetrics(updatedActiveChartMetrics);
        defaultState.activeChartMetrics = updatedActiveChartMetrics;
        liveCfg.defaultState.activeChartMetrics = updatedActiveChartMetrics;
      }
    });
    defaultState.displayedMetricKeys = clonedDisplayedMetricKeys;
    track('Dash Overview - Change Card Metric', { isSuperUser, metric: metricKey }, user.user);
  };

  const handleOnClickCard = (metricKey: string, cardIndex: number) => {
    const key = metricKey as keyof IDashboardHomeCardMetrics;
    const clonedActiveChartMetrics = [...activeChartMetrics];
    const metric = find(clonedActiveChartMetrics, { key });
    if (!metric) {
      const selectedMetric = cfg.cards.metrics[key];
      clonedActiveChartMetrics.push({
        key: metricKey,
        label: selectedMetric.label,
        valueType: selectedMetric.valueType,
        chartColorIndex: cardIndex,
        chartType: selectedMetric.chartType,
        yScale: selectedMetric.yScale || 'base',
      });
      setActiveChartMetrics(clonedActiveChartMetrics);
      defaultState.activeChartMetrics = clonedActiveChartMetrics;
      liveCfg.defaultState.activeChartMetrics = clonedActiveChartMetrics;
    } else {
      const filteredActiveChartMetrics = clonedActiveChartMetrics.filter(
        (metric) => metric.key !== key,
      );
      setActiveChartMetrics(filteredActiveChartMetrics);
      defaultState.activeChartMetrics = filteredActiveChartMetrics;
      liveCfg.defaultState.activeChartMetrics = filteredActiveChartMetrics;
    }
    track('Dash Overview - Display Metric On Chart', { isSuperUser, metric: metricKey }, user.user);
  };

  const handlePeriodMenuToggle = () => {
    defaultState.isPeriodMenuOpen = !isPeriodMenuOpen;
    setIsPeriodMenuOpen(!isPeriodMenuOpen);
    track(
      'Dash Overview - Open/Close Period Drawer',
      { isSuperUser, open: !isPeriodMenuOpen },
      user.user,
    );
  };

  const handleFilterMenuToggle = () => {
    defaultState.isFilterMenuOpen = !isFilterMenuOpen;
    setIsFilterMenuOpen(!isFilterMenuOpen);
    track(
      'Dash Overview - Open/Close Filter Drawer',
      { isSuperUser, open: !isFilterMenuOpen },
      user.user,
    );
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const csvMetricKeys = cfg.cards.groupings[graphID] as (keyof IDashboardHomeCardMetrics)[];
  const csvMetricsCfg = cfg.cards.metrics;

  return (
    <Panels max vertical relative minWidthMain>
      <Panel largeHorizontalPadding noVerticalPadding>
        <Cols wrap gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col style={{ minWidth: '248px' }}>
            <DashboardSelector />
          </Col>
          <Col>
            <Cols gap={spacing.extraLarge} style={{ alignItems: 'center' }}>
              <Col>
                <DashboardFilterMenuToggle
                  activeFilters={activeFilters}
                  handleOnClick={handleFilterMenuToggle}
                  isOpen={isFilterMenuOpen}
                />
              </Col>
              <Col>
                <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                  <Col>
                    <MenuToggle
                      isActive={isPeriodMenuOpen}
                      onClick={handlePeriodMenuToggle}
                      value={dateRange.label || 'Custom'}
                      SvgIcon={CalendarIcon}
                      style={{ height: '30px' }}
                    />
                  </Col>
                  <Col>
                    <DashboardTrendsSticker
                      isActive={isCompareEnabled}
                      style={{ padding: '5px' }}
                      onClick={() => setIsCompareEnabled(!isCompareEnabled)}
                    />
                  </Col>
                </Cols>
              </Col>
              <Col>
                <TextButton size='small' weight='base' onClick={getDashboardOverviewData}>
                  <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                    <Col displayFlex>
                      <RefreshIcon color={publicaColors.lightBlue} />
                    </Col>
                    <Col>Refresh</Col>
                  </Cols>
                </TextButton>
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Panel>
      <Panel
        largeHorizontalPadding
        smallVerticalPadding
        style={{ display: isPeriodMenuOpen || isFilterMenuOpen ? 'block' : 'none' }}
        enableSize
      >
        <Rows
          style={{
            border: `1px solid ${publicaColors.lightGray}`,
            borderRadius: '4px',
            backgroundColor: publicaColors.white,
          }}
        >
          <Row
            horizontalPadding
            extraSmallVerticalPadding
            style={{ display: isPeriodMenuOpen ? 'block' : 'none' }}
          >
            <DashboardPeriods
              calStartDate={calStartDate}
              calEndDate={calEndDate}
              calCompareStartDate={calCompareStartDate}
              calCompareEndDate={calCompareEndDate}
              isCompareEnabled={isCompareEnabled}
              handleApplyDatesChange={handleApplyDatesChange}
              handleApplyCompareDatesChange={handleApplyCompareDatesChange}
              dateRange={dateRange}
              handleToggleDateRange={handleToggleDateRange}
              setIsCompareEnabled={setIsCompareEnabled}
            />
          </Row>
          <Row
            horizontalPadding
            extraSmallVerticalPadding
            style={{ display: isFilterMenuOpen ? 'block' : 'none' }}
          >
            <DashboardFilters
              activeFilters={activeFilters}
              filters={filters}
              filtersCfg={cfg.filters}
              handleAcceptFilter={handleAcceptFilter}
              handleChangeTimezone={handleChangeTimezone}
              handleClearAllFilters={handleClearAllFilters}
              isDemandRefreshing={isDemandRefreshing}
              loadState={filtersLoadState}
              orderedFilterKeys={cfg.orderedFilterKeys as FilterPillKey[]}
              selectedFilters={selectedFilters}
              timezone={timezone}
              isDemandSourceUsingBidderType={isDemandSourceUsingBidderType.current}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding enableSize>
        <Rows
          style={{
            border: `1px solid ${publicaColors.lightGray}`,
            borderRadius: '4px',
            backgroundColor: publicaColors.white,
          }}
        >
          <Row style={{ padding: '8px 16px 0px' }}>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Col>
                <Text small bold>
                  Performances
                </Text>
              </Col>
              <Col>
                <CSVDownloadButton
                  isDisabled={loadState !== LoadingStateHasData && !isEmpty(data.MainData)}
                  columns={generateCSVColumns(csvMetricKeys, csvMetricsCfg)}
                  datas={() =>
                    formatDataToCSV(data.MainData, data.CompareData, isCompareEnabled, grouping)
                  }
                  filename={'Performances Overview'}
                  title={generateCSVTitle(activeFilterParams)}
                />
              </Col>
            </Cols>
          </Row>
          <Row style={{ padding: '8px 16px 16px' }}>
            <DashboardCardsV2
              availableMetrics={extractViableMetrics(cards, graphID)}
              displayedMetricKeys={displayedMetricKeys}
              cardMetrics={data.CardMetrics}
              loadState={loadState}
              isCompareEnabled={isCompareEnabled}
              activeChartMetrics={activeChartMetrics}
              handleOnClickCard={handleOnClickCard}
              handleOnChangeCardMetric={handleOnChangeCardMetric}
            />
          </Row>
          <Row smallVerticalPadding paddingRight='72px'>
            <DateGroupToggles
              activeGrouping={grouping}
              activeDateRange={dateRange}
              handleSelectGrouping={handleSelectDateGroup}
            />
          </Row>
          <Row>
            <CompareChart
              loadState={loadState}
              isCompareEnabled={isCompareEnabled}
              data={mergeCompareData(data.MainData, data.CompareData, isCompareEnabled, grouping)}
              metrics={activeChartMetrics}
              metricsConfig={cards.metrics}
              grouping={grouping}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding>
        <Panels max relative style={{ gap: spacing.base }}>
          <Panel highlighted flex horizontalPadding smallVerticalPadding horizontalScroll>
            <TopChannelsTable
              params={topChannelsParams}
              columns={cfg.tables.topChannels.columns}
              rowsPerPage={tableRowsPerPage}
              setRowsPerPage={setTableRowsPerPage}
              initialLoadStart={initialLoadStart}
            />
          </Panel>
          <Panel highlighted flex horizontalPadding smallVerticalPadding horizontalScroll>
            <TopDemandSourcesTable
              params={topDemandSourcesParams}
              columns={cfg.tables.topDemandSources.columns}
              activeColumnKeys={cfg.tables.topDemandSources.activeMetricKeys}
              rowsPerPage={tableRowsPerPage}
              setRowsPerPage={setTableRowsPerPage}
              initialLoadStart={initialLoadStart}
            />
          </Panel>
        </Panels>
      </Panel>
      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={handleSnackbarClose}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
